import React, { useState, useEffect } from 'react';
import api from '../../api/axiosInstance';
import { PermissionManager } from '../../utils/PermissionManager';

export interface UseSessionReturn {
  userId: string;
  hasNewPosts: boolean;
  setHasNewPosts: React.Dispatch<React.SetStateAction<boolean>>;
  hasNewErrorPosts: boolean;
  setHasNewErrorPosts: React.Dispatch<React.SetStateAction<boolean>>;
  latestVersion: string;
  checkSession: () => Promise<boolean>;
  getUserName: () => Promise<{ name: string; phone: string } | null>;
}

export function useSession(navigate: (path: string) => void): UseSessionReturn {
  const [userId, setUserId] = useState<string>('');
  const [hasNewPosts, setHasNewPosts] = useState<boolean>(false);
  const [hasNewErrorPosts, setHasNewErrorPosts] = useState<boolean>(false);
  const [latestVersion, setLatestVersion] = useState<string>('');

  // 페이지 로드 시 토큰 확인
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      const CRM_WEB_URL = process.env.REACT_APP_CRM_WEB_URL || 'https://crm.cahwindow.co.kr';
      alert('로그인이 필요합니다.\n로그인 페이지로 이동합니다.');
      const returnUrl = encodeURIComponent(window.location.origin + '/estimateForm');
      window.location.href = `${CRM_WEB_URL}/login?returnUrl=${returnUrl}`;
    }
  }, []);

  const checkSession = async () => {
    try {
      const userInfoRes = await api.get('/api/UserName');
      if (userInfoRes.data && userInfoRes.data.id) {
        setUserId(userInfoRes.data.id);
      }

      if (!PermissionManager.canAccessEstimateForm()) {
        alert('견적 페이지 접근 권한이 없습니다.');
        navigate('/');
        return false;
      }

      return true;
    } catch (error) {
      console.error('사용자 정보 가져오기 실패:', error);
      alert('인증 정보를 가져오는데 실패했습니다. 다시 로그인 해주세요.');
      navigate('/');
      return false;
    }
  };

  const getUserName = async (): Promise<{ name: string; phone: string } | null> => {
    try {
      const res = await api.get('/api/UserName');
      const userData = res.data;
      return {
        name: userData.name,
        phone: userData.phone_number || '1661-4830',
      };
    } catch (error) {
      console.error('사용자 정보 조회 중 오류:', error);
      return null;
    }
  };

  // userId가 설정되면 새 게시글 체크
  useEffect(() => {
    if (!userId) return;

    const checkNewPosts = async () => {
      try {
        const lastBoardCheck = localStorage.getItem(`lastBoardCheck_${userId}`);
        const checkTime = lastBoardCheck || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const response = await api.get('/api/board/check-new', {
          params: { lastCheck: checkTime },
        });
        setHasNewPosts(response.data.hasNewPosts);
      } catch (error) {
        console.error('새 게시글 체크 실패:', error);
      }
    };

    const checkNewErrorPosts = async () => {
      try {
        const lastErrorBoardCheck = localStorage.getItem(`lastErrorBoardCheck_${userId}`);
        const checkTime = lastErrorBoardCheck || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const response = await api.get('/api/error-board/check-new', {
          params: { lastCheck: checkTime },
        });
        setHasNewErrorPosts(response.data.hasNewPosts);
      } catch (error) {
        console.error('새 오류 게시글 체크 실패:', error);
      }
    };

    const fetchLatestVersion = async () => {
      try {
        const response = await api.get('/api/proposal-board/latest-version');
        setLatestVersion(response.data.version || '');
      } catch (error) {
        console.error('최신 버전 조회 실패:', error);
      }
    };

    checkNewPosts();
    checkNewErrorPosts();
    fetchLatestVersion();
  }, [userId]);

  return {
    userId,
    hasNewPosts,
    setHasNewPosts,
    hasNewErrorPosts,
    setHasNewErrorPosts,
    latestVersion,
    checkSession,
    getUserName,
  };
}
